import WhatsAppContactButton from '../components/whatsapp/WhatsAppContactButton';
import { CrmRecordHeader, CrmProfile, CrmSection, CrmFacts } from '../components/crm/CrmRecordLayout';
import { LEAD_STATO_LABEL, LEAD_SOURCE_LABEL } from '../types/lead';
import { CrmTabs } from '../components/crm/CrmUi';
import { useCrmNavigation } from '../hooks/useCrmNavigation';
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  AlertCircle,
  Trash2,
  ArrowRight,
  ExternalLink,
  Pencil,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from '../hooks/usePermissions';
import { leadsAPI } from '../utils/api';
import LeadStatoBadge from '../components/lead/LeadStatoBadge';
import LeadSourceBadge from '../components/lead/LeadSourceBadge';
import LeadActivityTimeline from '../components/lead/LeadActivityTimeline';
import LeadQuickActivityForm from '../components/lead/LeadQuickActivityForm';
import LeadEditForm from '../components/lead/LeadEditForm';
import LeadConvertModal from '../components/lead/LeadConvertModal';
import LeadTagChipEditor from '../components/lead/LeadTagChipEditor';
import LeadScoreBar from '../components/lead/LeadScoreBar';
import OpenapiDataPanel from '../components/lead/OpenapiDataPanel';
import ConfirmModal from '../components/ConfirmModal';

type Tab = 'profilo' | 'attivita' | 'conversione';

const TABS: { key: Tab; label: string }[] = [
  { key: 'profilo', label: 'Anagrafica' },
  { key: 'attivita', label: 'Attività' },
  { key: 'conversione', label: 'Conversione' },
];

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { returnTo } = useCrmNavigation('/leads');
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:edit');
  const canDelete = hasPermission('leads:delete');
  const canConvert = hasPermission('leads:convert');
  const [activeTab, setActiveTab] = useState<Tab>('profilo');
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isConvertOpen, setIsConvertOpen] = useState(false);

  const {
    data: lead,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      const res = await leadsAPI.getLead(id!);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: activitiesData, isLoading: isLoadingActivities } = useQuery({
    queryKey: ['lead-activities', id],
    queryFn: async () => {
      const res = await leadsAPI.listActivities(id!, { page: 1, limit: 100 });
      return res.data;
    },
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => leadsAPI.deleteLead(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead eliminato');
      navigate(returnTo);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-gray-200 rounded" />
          <div className="h-40 bg-gray-200 rounded-lg" />
          <div className="h-60 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError || !lead) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Lead non trovato</h2>
          <p className="text-gray-500 mb-6">
            Il lead richiesto non esiste o non hai i permessi per visualizzarlo.
          </p>
          <Link
            to={returnTo}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alla lista lead
          </Link>
        </div>
      </div>
    );
  }

  const isConverted = lead.stato === 'convertito' && !!lead.convertito_cliente_id;
  const activities = activitiesData?.data || [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <CrmRecordHeader kind="lead" record={lead} backTo={returnTo}
        badges={<><LeadStatoBadge stato={lead.stato} /><LeadSourceBadge source={lead.source} detail={lead.source_detail} /></>}
        actions={<>
          <WhatsAppContactButton phone={lead.telefono} />
          {!isEditing && canEdit && <button onClick={() => { setActiveTab('profilo'); setIsEditing(true); }} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"><Pencil className="h-4 w-4" />Modifica anagrafica</button>}
          {!isConverted && canConvert && <button onClick={() => setIsConvertOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><ArrowRight className="h-4 w-4" />Converti in cliente</button>}
          {canDelete && <button onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" />Elimina</button>}
        </>}
      />
      {/* Tab bar */}
      <CrmTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      <div hidden={activeTab !== 'profilo'} className="space-y-6">
        {isEditing && canEdit ? <LeadEditForm lead={lead} onUpdated={() => { refetch(); setIsEditing(false); }} onCancel={() => setIsEditing(false)} /> :
          <CrmProfile record={lead}>
            <CrmSection title="Qualificazione lead" description="Origine e responsabilità del contatto commerciale."><div className="space-y-5"><CrmFacts items={[
              ['Stato', LEAD_STATO_LABEL[lead.stato]], ['Score', <LeadScoreBar score={lead.score} />], ['Agente assegnato', lead.agente_nome],
              ['Origine', LEAD_SOURCE_LABEL[lead.source]], ['Dettaglio origine', lead.source_detail],
              ['Ultimo contatto', lead.ultimo_contatto_at ? new Date(lead.ultimo_contatto_at).toLocaleString('it-IT') : null],
              ['UTM source', lead.utm_source], ['UTM medium', lead.utm_medium], ['UTM campaign', lead.utm_campaign],
            ]} /><div><p className="mb-2 text-xs text-gray-500">Tag</p><LeadTagChipEditor leadId={lead.id} readonly={!canEdit} /></div></div></CrmSection>
          </CrmProfile>}
        {lead.tipo === 'azienda' && !isEditing && <OpenapiDataPanel lead={lead} />}
      </div>

      {activeTab === 'attivita' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline attività</h3>
            <LeadActivityTimeline
              activities={activities}
              isLoading={isLoadingActivities}
            />
          </div>
          <div>
            <LeadQuickActivityForm leadId={lead.id} />
          </div>
        </div>
      )}

      {activeTab === 'conversione' && (
        <div className="bg-white rounded-lg shadow p-6">
          {isConverted ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-700">
                <LeadStatoBadge stato="convertito" />
                <span className="text-sm">
                  Convertito il{' '}
                  {lead.convertito_at
                    ? new Date(lead.convertito_at).toLocaleString('it-IT')
                    : '—'}
                </span>
              </div>
              <Link
                to={`/clients/${lead.convertito_cliente_id}`}
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                Vai al cliente collegato
                {lead.convertito_cliente?.denominazione_completa
                  ? ` (${lead.convertito_cliente.denominazione_completa})`
                  : ''}
              </Link>
            </div>
          ) : (
            <div className="text-center py-8">
              <ArrowRight className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-600 mb-4">
                Converti questo lead in cliente. Verranno rilevati eventuali duplicati su
                email, C.F. e P.IVA.
              </p>

            </div>
          )}
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          isOpen={confirmDelete}
          title="Elimina lead"
          message={`Sei sicuro di voler eliminare il lead "${lead.denominazione_completa}"?`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate()}
          onClose={() => setConfirmDelete(false)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}

      {isConvertOpen && (
        <LeadConvertModal
          isOpen={isConvertOpen}
          leadId={lead.id}
          leadName={lead.denominazione_completa}
          onClose={() => setIsConvertOpen(false)}
          onConverted={(clienteId) => {
            setIsConvertOpen(false);
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            navigate(`/clients/${clienteId}`);
          }}
        />
      )}
    </div>
  );
}
